import { TableEmptyContent } from '@avanta/shared/components/TableEmptyContent';
import { Text } from '@avanta/shared/components/Text';
import { Card } from '@avanta/shared/components/ui/Card';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { Calendar } from 'lucide-react';
import { type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { RecentActivitySkeleton } from './RecentActivitySkeleton';
import { useEnterpriseActivity } from './useEnterpriseActivity';
import { ACTIVITY_ICON, ACTIVITY_TITLE_KEY, formatActivityDateTime } from './utils';

export const RecentActivity = (): ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);
  const { data: activities = [], isLoading } = useEnterpriseActivity();

  const renderContent = (): ReactNode => {
    if (isLoading) {
      return <RecentActivitySkeleton />;
    }

    if (activities.length === 0) {
      return (
        <TableEmptyContent
          className="h-full flex-1 py-0"
          emptyTitle={t('HomePage.recentActivity.empty')}
        />
      );
    }

    return activities.map(activity => {
      const Icon = ACTIVITY_ICON[activity.type] ?? Calendar;

      return (
        <div
          key={`${activity.type}-${activity.occurredAt}-${activity.actorEmail ?? ''}`}
          className="border-beige-200 flex gap-3 border-b py-3 first:pt-0 last:border-b-0 last:pb-0"
        >
          <Icon className="text-teal-600 mt-0.5 size-4 shrink-0" aria-hidden />
          <div className="min-w-0 flex flex-col gap-1">
            <Text variant="text-xs-medium" className="text-denim-500">
              {t(ACTIVITY_TITLE_KEY[activity.type])}
            </Text>
            {activity.actorEmail && (
              <Text
                variant="text-xs-regular"
                className="text-gray-500 max-w-56 min-w-0 wrap-break-word"
              >
                {activity.actorEmail}
              </Text>
            )}
            <Text variant="text-xs-regular" className="text-gray-500">
              {formatActivityDateTime(activity.occurredAt)}
            </Text>
          </div>
        </div>
      );
    });
  };

  return (
    <Card
      variant="primary"
      className="scrollbar-custom h-full w-full overflow-y-auto am-3xl:rounded-2xl am-md:px-4 am-md:py-6 *:data-[slot=card-content]:flex *:data-[slot=card-content]:min-h-0 *:data-[slot=card-content]:flex-1 *:data-[slot=card-content]:flex-col"
    >
      <div className="flex min-h-0 flex-1 flex-col">{renderContent()}</div>
    </Card>
  );
};
